import { useMutation, useQueryClient } from '@tanstack/react-query';
import { businessReportsApi } from '@/api/businessReports';
import { Checkbox } from '@/components/ui/checkbox';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const EXCLUDE_WARNING =
  'Once unchecked, this expense will not be included in P&L, cash flow, or any other financial report calculations.';

type Props = {
  expenseId: string;
  includedInReports: boolean;
};

export function CentralExpenseReportInclusionCheckbox({ expenseId, includedInReports }: Props) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const canEdit =
    user?.role === 'HOSPITAL_ADMIN' ||
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'BUSINESS_ANALYST';

  const mutation = useMutation({
    mutationFn: (included: boolean) =>
      businessReportsApi.setCentralExpenseReportInclusion(expenseId, included),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['pnl-expense-breakdown'] });
      void queryClient.invalidateQueries({ queryKey: ['cash-flow-breakdown'] });
      void queryClient.invalidateQueries({ queryKey: ['hospital-pnl'] });
      void queryClient.invalidateQueries({ queryKey: ['hospital-cash-flow'] });
    },
  });

  const handleChange = (checked: boolean) => {
    if (!canEdit || mutation.isPending) return;
    if (!checked) {
      const ok = window.confirm(EXCLUDE_WARNING);
      if (!ok) return;
    }
    mutation.mutate(checked);
  };

  return (
    <Checkbox
      checked={includedInReports}
      disabled={!canEdit || mutation.isPending}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onCheckedChange={handleChange}
      aria-label={includedInReports ? 'Included in reports' : 'Excluded from reports'}
      title={
        canEdit
          ? includedInReports
            ? 'Included in report calculations'
            : 'Excluded from report calculations'
          : undefined
      }
      className={cn('shrink-0 mt-0.5', !includedInReports && 'opacity-60')}
    />
  );
}
